import { makeStyles } from '@material-ui/core';

import { getHoverAreaSize } from '../utils';

const MIN_INPUT_WIDTH = 47;
const INPUT_HEIGHT = 20;

const MARKER_STYLES = {
  minWidth: MIN_INPUT_WIDTH,
  height: INPUT_HEIGHT,
  borderRadius: 4,
};

export const useHandleStyles = makeStyles(({ isLightTheme, palette }) => ({
  root: {
    fontSize: '12px',
    position: 'absolute',
    '&:hover': {
      borderColor: palette.primary.main,
    },
    '&:active': {
      borderColor: palette.primary.main,
    },
    '&:hover $handle': {
      borderColor: palette.primary.main,
    },
    '&:active $handle': {
      background: palette.primary.dark,
      borderColor: palette.primary.main,
    },
    '&:hover $inputRoot:not($hasError)': {
      background: palette.primary.dark,
      border: `1px solid ${palette.primary.main}`,
    },
    '&:hover $inputRoot $hasError': {
      background: `${palette.background.b6} !important`,
      border: `1px solid ${palette.primary.main}`,
    },
    '&:active $inputRoot': {
      background: palette.primary.dark,
      border: `1px solid ${palette.primary.main}`,
    },
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover $leftArrow, &:active $leftArrow': {
      visibility: 'visible',
    },
    '&:hover $rightArrow, &:active $rightArrow': {
      visibility: 'visible',
    },
  },
  hasError: {
    border: `1px solid ${palette.error.main} !important`,
    '&.Mui-focused': {
      background: `${palette.background.b6} !important`,
    },
    '&:hover': {
      background: palette.background.b6,
    },
    '&:hover $inputRoot': { background: palette.background.b6 },
  },

  horizontalHeadRoot: {
    top: 0,
    bottom: 0,
    right: 5, // 5px by re-resizable
  },
  horizontalTailRoot: {
    top: 0,
    bottom: 0,
    left: 5, // 5px by re-resizable
  },
  verticalHeadRoot: {
    left: 0,
    right: 0,
    bottom: 5, // 5px by re-resizable
  },
  verticalTailRoot: {
    left: 0,
    right: 0,
    top: 5, // 5px by re-resizable
  },
  handle: {
    position: 'relative',
    background: isLightTheme ? '#B1B1B1' : '#414141',
    '& > p': {
      fontSize: '12px',
      textAlign: 'center',
      lineHeight: '20px',
    },
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:before': {
      position: 'absolute',
      left: '50%',
      top: '50%',
      height: ({ distance, hoverAreaHeight }) =>
        getHoverAreaSize({ initialSize: hoverAreaHeight, distance }),
      width: ({ distance, hoverAreaWidth }) =>
        getHoverAreaSize({ distance, initialSize: hoverAreaWidth }),
      content: '""',
      transform: 'translate(-50%, -50%)',
      zIndex: 100,
    },
  },
  horizontalHeadHandle: ({ minimal }) =>
    !minimal
      ? MARKER_STYLES
      : {
          ...MARKER_STYLES,
          borderRadius: 'unset',
          borderTopLeftRadius: 2,
          borderBottomLeftRadius: 2,
        },
  horizontalTailHandle: ({ minimal }) =>
    !minimal
      ? MARKER_STYLES
      : {
          ...MARKER_STYLES,
          borderRadius: 'unset',
          borderTopRightRadius: 2,
          borderBottomRightRadius: 2,
        },
  verticalHeadHandle: ({ minimal }) =>
    !minimal
      ? MARKER_STYLES
      : {
          ...MARKER_STYLES,
          borderRadius: 'unset',
          borderTopLeftRadius: 2,
          borderTopRightRadius: 2,
        },
  verticalTailHandle: ({ minimal }) =>
    !minimal
      ? MARKER_STYLES
      : {
          ...MARKER_STYLES,
          borderRadius: 'unset',
          borderBottomRightRadius: 2,
          borderBottomLeftRadius: 2,
        },
  inputRoot: {
    borderRadius: 4,
    minWidth: MIN_INPUT_WIDTH,
    zIndex: 101,
    height: '20px',
    fontSize: '12px',
    border: `1px solid ${palette.primary.text9}`,
    '&:hover': {
      border: `1px solid ${palette.primary.main}`,
    },
    '&:active': {
      textDecoration: 'underline',
    },
    '&::before': { borderWidth: 0 },
    '&::after': { borderWidth: '1px !important' },
    '& .MuiInputBase-input': {
      padding: 0,
      height: '100%',
      textAlign: 'center',
    },
    '&:hover::before': {
      borderWidth: '1px !important',
    },
    '&.Mui-focused': {
      textDecoration: 'underline',
      background: palette.primary.dark,
      border: `1px solid ${palette.primary.main}`,
    },
  },
  minimalHandle: {
    background: `${palette.primary.text9} !important`,
    width: '8px !important',
    minWidth: '8px !important',
    minHeight: 18,
    height: '18px !important',
    '& .MuiInput-underline.Mui-error:after': {
      border: 0,
    },
    '&:before': {
      width: '0px !important',
      height: '0px !important',
    },

    '& $horizontalHeadHandle': {
      borderRadius: '0px !important',
    },
  },
  medium: {
    '& $minimalHandle': {
      minHeight: '24px !important',
    },
    '& $minimalVerticalHandle': {
      minHeight: '8px !important',
      minWidth: '24px !important',
    },
  },
  large: {
    '& $minimalHandle': {
      minHeight: '48px !important',
    },
    '& $minimalVerticalHandle': {
      minHeight: '8px !important',
      minWidth: '48px !important',
    },
  },
  minimalVerticalHandle: {
    width: 18,
    minWidth: '18px !important',
    minHeight: 8,
    height: '8px !important',
  },
  minimalHandleRoot: {
    borderColor: palette.primary.text9,
    zIndex: 100,
    '&:not(:hover)': {
      border: 'none',
    },
  },
  leftArrow: {
    visibility: 'hidden',
    zIndex: 101,
    position: 'absolute',
    left: -16,
  },
  rightArrow: {
    visibility: 'hidden',
    zIndex: 101,
    position: 'absolute',
    right: -15,
  },
  reversedLeftArrow: {
    left: '50%',
    transform: 'translateX(-50%) rotate(90deg)',
    top: -16,
  },
  reversedRightArrow: {
    left: '50%',
    transform: 'translateX(-50%) rotate(90deg)',
    bottom: -15,
  },
  isInvisible: {
    display: 'none',
  },
}));
